import { Grid, Alert } from '@mui/material'
import { CreateProductOrderReceiptNewProductDto, ProductCatalogDto } from '@services/dtos'
import SelectField from '@components/inputs/SelectField'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { productOrderReceiptNewProductSchema } from '@utils/schemas'
import { useState } from 'react'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { productCatalogService } from '@services/ProductCatalogService'
import { AddWrapperInput } from '@components/inputs/AddWrapperInput'
import ProductCatalogForm from '../../productCatalog/ProductCatalogForm'
import { FormHeader } from '../../FormHeader'
import { useAppDispatch } from '@hooks/reduxHooks'
import { addSelectedNewProduct } from '@state/reducers/productOrderReceiptReducer'

type Props = {
  onClose: () => void
  orderProducts: ProductCatalogDto[]
  onSubmit: (data: CreateProductOrderReceiptNewProductDto) => void
}

export function ProductOrderReceiptNewProductForm({ onClose, orderProducts, onSubmit }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedNewProduct, setSelectedNewProduct] = useState<ProductCatalogDto | null>(null)
  const { control, handleSubmit, watch, formState: { errors }, setValue } = useForm<CreateProductOrderReceiptNewProductDto>({
    resolver: yupResolver(productOrderReceiptNewProductSchema),
    defaultValues: orderProducts.length === 1 ? { baseProductId: orderProducts[0].id } : {}
  })

  const dispatch = useAppDispatch()
  const baseProductId = watch('baseProductId')
  const baseProductSelected = baseProductId
    ? orderProducts.find(product => product.id === baseProductId)
    : null
  const fetchProductCatalogs = async (search: string): Promise<ProductCatalogDto[]> => {
    if (!baseProductSelected) {
      return []
    }
    const response = await productCatalogService.getAll({ 
      search, 
      limit: 20, 
      type: baseProductSelected.type, 
      genericName: baseProductSelected.genericName,
      excludedIds: [baseProductId]
    })
    return response?.datas ?? []
  }

  const onSubmitForm = async (data: CreateProductOrderReceiptNewProductDto) => {
    setIsSubmitting(true)
    if (selectedNewProduct) {
      dispatch(addSelectedNewProduct(selectedNewProduct))
    }
    onSubmit(data)
    setIsSubmitting(false)
    onClose()
  }

  return (<div className="flex w-full h-full p-4">
    <form 
      onSubmit={(e) => {
        e.stopPropagation()
        handleSubmit(onSubmitForm)(e)
      }}
      className="w-full">
      <FormHeader
        isSubmitting={isSubmitting}
        onClose={onClose}
        title={'Réception'}
      />

      <div className="max-w-screen-md mx-auto mt-4">
        <fieldset className="border border-gray-200 p-4 rounded-lg">
          <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
            Produit de remplacement
          </legend>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SelectField
                control={control}
                name={'baseProductId'}
                options={orderProducts.map(({ id, name }) => ({
                  label: name,
                  value: id,
                }))}
                value={baseProductId}
                label="Produit initial"
              />
            </Grid>

            {baseProductId && (<Grid item xs={12}>
              <AddWrapperInput
                legend="Produit"
                addTooltipTitle="Ajouter un produit"
                dialogContent={(close) => <ProductCatalogForm 
                  onClose={close} 
                  defaultValues={{
                    type: baseProductSelected?.type,
                    genericName: baseProductSelected?.genericName,
                  }} 
                />}
                formMaxWidth="md"
                input={
                  <>
                    <ServerAutocomplete<ProductCatalogDto>
                      fetchService={fetchProductCatalogs}
                      onChange={(value) => {
                        setValue('productId', value?.id ?? '')
                        setSelectedNewProduct(value)
                      }}
                      getValue={(value) => value?.id?.toString() || null}
                      getOptionLabel={(value) => {
                        const product = value as ProductCatalogDto
                        return product ? product.name : ''
                      }}
                      debounceMs={300}
                      placeholder="Rechercher un produit"
                      label="Produit réceptionné"
                      minSearchLength={0}
                      error={!!errors.productId}
                      helperText={errors.productId?.message}
                    />
                    <Alert severity="info">Seuls les produits du même type et avec la même DCI sont autorisés.</Alert>
                  </>
                }
              />
            </Grid>)}
          </Grid>
        </fieldset>
      </div>
    </form>
  </div>
)}
